import { useState, useEffect, useRef, useCallback } from "react";

const W = 390, H = 720, MARGIN = 30;

/* ═══════════════════════════════════════════════════════════
   AUDIO ENGINE  –  100% procedural Web Audio API
   Zero external files. All sounds synthesized in real-time.
═══════════════════════════════════════════════════════════ */
class AudioEngine {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.musicGain = null;
    this.sfxGain = null;
    this.musicNodes = [];
    this.intensity = 0;
    this.ready = false;
  }

  init() {
    if (this.ready) return;
    try {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.ctx.createGain();
      this.masterGain.gain.value = 0.72;
      this.masterGain.connect(this.ctx.destination);
      this.sfxGain = this.ctx.createGain();
      this.sfxGain.gain.value = 0.6;
      this.sfxGain.connect(this.masterGain);
      this.musicGain = this.ctx.createGain();
      this.musicGain.gain.value = 0.3;
      this.musicGain.connect(this.masterGain);
      this.ready = true;
      this._startMusic();
    } catch (e) { /* AudioContext unavailable */ }
  }

  stop() {
    if (!this.ready) return;
    this.musicNodes.forEach(n => { try { n.stop && n.stop(); n.disconnect && n.disconnect(); } catch(_){} });
    this.musicNodes = [];
  }

  setIntensity(v) { this.intensity = Math.min(1, v); }

  // ── Internal helpers ──────────────────────────────────
  _osc(type, freq, dur, gain = 0.5, startDelay = 0, pitchEnd = null) {
    if (!this.ready) return;
    const ac = this.ctx, t = ac.currentTime + startDelay;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = type; o.frequency.value = freq;
    if (pitchEnd) o.frequency.exponentialRampToValueAtTime(pitchEnd, t + dur);
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    o.connect(g); g.connect(this.sfxGain);
    o.start(t); o.stop(t + dur + 0.01);
  }

  _noise(dur, gain = 0.4, hiPass = 500, startDelay = 0) {
    if (!this.ready) return;
    const ac = this.ctx, t = ac.currentTime + startDelay;
    const buf = ac.createBuffer(1, ac.sampleRate * dur, ac.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
    const src = ac.createBufferSource();
    src.buffer = buf;
    const filt = ac.createBiquadFilter();
    filt.type = 'highpass'; filt.frequency.value = hiPass;
    const g = ac.createGain();
    g.gain.setValueAtTime(gain, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + dur);
    src.connect(filt); filt.connect(g); g.connect(this.sfxGain);
    src.start(t); src.stop(t + dur + 0.01);
  }

  _distort(amount = 50) {
    if (!this.ready) return null;
    const ws = this.ctx.createWaveShaper();
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i * 2) / 256 - 1;
      curve[i] = ((Math.PI + amount) * x) / (Math.PI + amount * Math.abs(x));
    }
    ws.curve = curve;
    return ws;
  }

  // ── SFX ──────────────────────────────────────────────
  shoot() {
    if (!this.ready) return;
    this._osc('square', 900, 0.055, 0.16);
    this._osc('sine', 1400, 0.04, 0.10, 0, 600);
  }

  hit(isElite) {
    if (!this.ready) return;
    this._noise(0.065, isElite ? 0.28 : 0.20, isElite ? 800 : 1200);
    this._osc('sine', isElite ? 140 : 220, 0.1, isElite ? 0.15 : 0.1);
  }

  die(isElite) {
    if (!this.ready) return;
    const freqs = isElite ? [260, 180, 120, 75] : [310, 200, 145];
    freqs.forEach((f, i) => {
      const ac = this.ctx, t = ac.currentTime + i * 0.032;
      const o = ac.createOscillator(); const g = ac.createGain();
      const dist = this._distort(isElite ? 60 : 30);
      o.type = 'sawtooth'; o.frequency.value = f;
      o.frequency.exponentialRampToValueAtTime(f * 0.28, t + 0.28);
      g.gain.setValueAtTime(isElite ? 0.3 : 0.2, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.32);
      if (dist) { o.connect(dist); dist.connect(g); } else { o.connect(g); }
      g.connect(this.sfxGain); o.start(t); o.stop(t + 0.38);
    });
    this._noise(isElite ? 0.38 : 0.2, isElite ? 0.42 : 0.26, isElite ? 80 : 350);
    if (isElite) {
      this._osc('sine', 60, 0.25, 0.5, 0.05, 28);
    }
  }

  playerHurt() {
    if (!this.ready) return;
    this._noise(0.18, 0.5, 60);
    this._osc('sawtooth', 85, 0.22, 0.32, 0, 40);
    this._osc('square', 400, 0.08, 0.18, 0.05, 100);
  }

  comboBreak() {
    if (!this.ready) return;
    [440, 330, 220, 165].forEach((f, i) => {
      this._osc('triangle', f, 0.22, 0.16, i * 0.075);
    });
  }

  comboMilestone(combo) {
    if (!this.ready) return;
    const scale = combo >= 60 ? 2 : combo >= 30 ? 1.5 : 1;
    const notes = [523, 659, 784, 1047, 1319];
    notes.forEach((f, i) => this._osc('sine', f * scale, 0.28, 0.2, i * 0.065));
  }

  activateOverdrive() {
    if (!this.ready) return;
    const ac = this.ctx;
    // Rising distorted sweep
    const o = ac.createOscillator(); const g = ac.createGain();
    const dist = this._distort(90);
    o.type = 'sawtooth';
    o.frequency.setValueAtTime(80, ac.currentTime);
    o.frequency.exponentialRampToValueAtTime(2800, ac.currentTime + 0.45);
    g.gain.setValueAtTime(0.38, ac.currentTime);
    g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.6);
    if (dist) { o.connect(dist); dist.connect(g); } else { o.connect(g); }
    g.connect(this.sfxGain); o.start(); o.stop(ac.currentTime + 0.65);
    // Sub bass punch
    this._osc('sine', 55, 0.7, 0.65, 0.28, 25);
    // White noise burst
    this._noise(0.15, 0.55, 150, 0.22);
    // Harmonic stabs
    [220, 440, 880].forEach((f, i) => this._osc('square', f, 0.12, 0.22, 0.3 + i * 0.06));
  }

  waveUp(wave) {
    if (!this.ready) return;
    const base = 220 * Math.pow(1.055, wave);
    [1, 1.25, 1.5, 2, 2.5].forEach((m, i) => this._osc('triangle', base * m, 0.32, 0.18, i * 0.08));
  }

  dashSound() {
    if (!this.ready) return;
    this._noise(0.09, 0.28, 2000);
    this._osc('sine', 600, 0.08, 0.12, 0, 1200);
  }

  // ── Procedural Music ─────────────────────────────────
  _startMusic() {
    if (!this.ready) return;
    const ac = this.ctx;

    // Ambient drone chord (minor 7th)
    [55, 82.5, 110, 138.6, 165].forEach((f, i) => {
      const o = ac.createOscillator(); const g = ac.createGain();
      const filt = ac.createBiquadFilter();
      o.type = 'sine'; o.frequency.value = f;
      o.detune.value = (Math.random() - 0.5) * 8;
      filt.type = 'lowpass'; filt.frequency.value = 800;
      g.gain.value = 0.10 - i * 0.012;
      o.connect(filt); filt.connect(g); g.connect(this.musicGain);
      o.start();
      this.musicNodes.push(o);
    });

    // Bass sequencer
    this._runBassSeq();
    // Drum machine
    this._runDrums();
    // Lead arpeggio
    this._runArp();
  }

  _runBassSeq() {
    if (!this.ready) return;
    const ac = this.ctx;
    const seq = [55, 55, 73.4, 82.5, 55, 55, 55, 61.7, 49, 55, 73.4, 55, 55, 69.3, 55, 55];
    let i = 0;
    const stepMs = 920 / 2;
    const id = setInterval(() => {
      if (!this.ready) return;
      const f = seq[i % seq.length] * (this.intensity > 0.55 ? 2 : 1);
      const o = ac.createOscillator(); const g = ac.createGain();
      const d = this._distort(15 + this.intensity * 55);
      o.type = 'sawtooth'; o.frequency.value = f;
      g.gain.setValueAtTime(0.32 + this.intensity * 0.14, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.38);
      if (d) { o.connect(d); d.connect(g); } else { o.connect(g); }
      g.connect(this.musicGain); o.start(); o.stop(ac.currentTime + 0.42);
      i++;
    }, stepMs);
    this.musicNodes.push({ stop: () => clearInterval(id), disconnect: () => {} });
  }

  _runDrums() {
    if (!this.ready) return;
    const ac = this.ctx;
    const BPM = 138;
    const stepMs = (60000 / BPM) / 2;
    const kick  = [1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0];
    const snare = [0,0,1,0,0,0,1,0,0,0,1,0,0,0,1,0];
    const hat   = [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1];
    let beat = 0;

    const playKick = () => {
      const o = ac.createOscillator(); const g = ac.createGain();
      o.type = 'sine';
      o.frequency.setValueAtTime(160, ac.currentTime);
      o.frequency.exponentialRampToValueAtTime(38, ac.currentTime + 0.18);
      g.gain.setValueAtTime(0.62, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.24);
      o.connect(g); g.connect(this.musicGain); o.start(); o.stop(ac.currentTime + 0.28);
    };
    const playSnare = () => {
      const len = 0.15;
      const buf = ac.createBuffer(1, ac.sampleRate * len, ac.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < d.length; j++) d[j] = Math.random() * 2 - 1;
      const src = ac.createBufferSource(); src.buffer = buf;
      const filt = ac.createBiquadFilter(); filt.type = 'bandpass'; filt.frequency.value = 1400 + this.intensity * 400;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.28 + this.intensity * 0.12, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + len);
      // tone layer
      const tone = ac.createOscillator(); const tg = ac.createGain();
      tone.type = 'triangle'; tone.frequency.value = 200;
      tg.gain.setValueAtTime(0.14, ac.currentTime);
      tg.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.08);
      tone.connect(tg); tg.connect(this.musicGain); tone.start(); tone.stop(ac.currentTime + 0.1);
      src.connect(filt); filt.connect(g); g.connect(this.musicGain); src.start(); src.stop(ac.currentTime + len + 0.01);
    };
    const playHat = () => {
      if (this.intensity < 0.18 && beat % 4 !== 0) return;
      const len = 0.045;
      const buf = ac.createBuffer(1, ac.sampleRate * len, ac.sampleRate);
      const d = buf.getChannelData(0);
      for (let j = 0; j < d.length; j++) d[j] = Math.random() * 2 - 1;
      const src = ac.createBufferSource(); src.buffer = buf;
      const filt = ac.createBiquadFilter(); filt.type = 'highpass'; filt.frequency.value = 8000;
      const g = ac.createGain();
      g.gain.setValueAtTime(0.13 + this.intensity * 0.06, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + len);
      src.connect(filt); filt.connect(g); g.connect(this.musicGain); src.start(); src.stop(ac.currentTime + len + 0.01);
    };

    const id = setInterval(() => {
      const step = beat % 16;
      if (kick[step])  playKick();
      if (snare[step]) playSnare();
      if (hat[step])   playHat();
      beat++;
    }, stepMs);
    this.musicNodes.push({ stop: () => clearInterval(id), disconnect: () => {} });
  }

  _runArp() {
    if (!this.ready) return;
    const ac = this.ctx;
    // A minor pentatonic-ish
    const scale = [0, 3, 7, 10, 12, 15, 19, 22, 24];
    let idx = 0;
    const id = setInterval(() => {
      if (this.intensity < 0.12) { idx++; return; }
      const semi = scale[idx % scale.length];
      const oct = this.intensity > 0.7 ? 2 : this.intensity > 0.4 ? 1.5 : 1;
      const f = 110 * Math.pow(2, semi / 12) * oct;
      const o = ac.createOscillator(); const g = ac.createGain();
      const filt = ac.createBiquadFilter();
      o.type = 'square'; o.frequency.value = f;
      filt.type = 'lowpass'; filt.frequency.value = 1600 + this.intensity * 2600;
      g.gain.setValueAtTime(0.09 + this.intensity * 0.07, ac.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + 0.2);
      o.connect(filt); filt.connect(g); g.connect(this.musicGain);
      o.start(); o.stop(ac.currentTime + 0.22);
      idx++;
    }, Math.max(60, 110 - this.intensity * 42));
    this.musicNodes.push({ stop: () => clearInterval(id), disconnect: () => {} });
  }
}

const audioEngine = new AudioEngine();

/* ═══════════════════════════════════════════════════════════
   UTILITIES
═══════════════════════════════════════════════════════════ */
function dist(a, b) { return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2); }
function norm(v) { const d = Math.sqrt(v.x ** 2 + v.y ** 2) || 1; return { x: v.x / d, y: v.y / d }; }
function lerp(a, b, t) { return a + (b - a) * t; }
function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }
function rand(a, b) { return a + Math.random() * (b - a); }

function edgeSpawn() {
  const e = Math.floor(Math.random() * 4), m = MARGIN + 14;
  if (e === 0) return { x: rand(m, W - m), y: m };
  if (e === 1) return { x: rand(m, W - m), y: H - m };
  if (e === 2) return { x: m, y: rand(m, H - m) };
  return { x: W - m, y: rand(m, H - m) };
}

function makeEnemy(wave) {
  const pos = edgeSpawn();
  const isElite = wave > 4 && Math.random() < clamp(0.1 + wave * 0.025, 0, 0.38);
  // Orbiter unlocks at wave 3, chance grows with wave
  const isOrbiter = !isElite && wave >= 3 && Math.random() < clamp(0.08 + wave * 0.03, 0, 0.42);
  const hp = isElite ? 90 + wave * 20 : isOrbiter ? 45 + wave * 14 : 30 + wave * 11;
  const speed = isElite ? clamp(1.8 + wave * 0.11, 0, 4.0) : isOrbiter ? clamp(1.2 + wave * 0.09, 0, 2.8) : clamp(1.5 + wave * 0.13, 0, 3.6);
  return {
    ...pos, hp, maxHp: hp, speed, radius: isElite ? 19 : isOrbiter ? 14 : 12,
    isElite, isOrbiter, type: isElite ? 'elite' : isOrbiter ? 'orbiter' : 'normal',
    angle: 0, vx: 0, vy: 0, flashTimer: 0,
    shootTimer: isElite ? rand(1.2, 2.6) : 99,
    orbitAngle: Math.random() * Math.PI * 2,
    // Orbiter-specific: pivot point that slowly tracks player
    pivotX: pos.x, pivotY: pos.y,
    orbitRadius: rand(55, 110), orbitSpeed: (Math.random() < 0.5 ? 1 : -1) * rand(1.2, 2.2),
    spawnPop: 0.3,
  };
}

function makeBoss(wave) {
  // Spawn near an edge but not too close to corners
  const side = Math.floor(Math.random() * 4);
  let bx, by;
  const m = MARGIN + 40;
  if (side === 0) { bx = W / 2; by = m; }
  else if (side === 1) { bx = W / 2; by = H - m; }
  else if (side === 2) { bx = m; by = H / 2; }
  else { bx = W - m; by = H / 2; }
  const hp = 800 + wave * 120;
  return {
    x: bx, y: by, hp, maxHp: hp, speed: clamp(1.0 + wave * 0.06, 0, 2.2),
    radius: 36, isElite: false, isOrbiter: false, isBoss: true, type: 'boss',
    angle: 0, vx: 0, vy: 0, flashTimer: 0,
    orbitAngle: 0, shootTimer: 0.8, shootPhase: 0,
    spawnPop: 0.6, wave,
  };
}

/* ─── POWER-UPS ──────────────────────────────────────────── */
// Drop chances per enemy type: boss 60%, elite 30%, orbiter 20%, normal 12%
const POWERUP_DROP_CHANCE = { boss: 0.60, elite: 0.30, orbiter: 0.20, normal: 0.12 };
const TRIPLE_SHOT_DURATION = 10; // seconds

function makePowerup(x, y) {
  return {
    x, y, type: 'tripleShot',
    radius: 13,
    life: 12,      // disappears after 12 s if not collected
    maxLife: 12,
    pulseAngle: Math.random() * Math.PI * 2,
    vy: -0.4,      // gentle float upward
  };
}

/* ─── HIGHSCORE ──────────────────────────────────────────── */
const HS_KEY = 'overdrive_arena_hs';

function loadLocalHighscore() {
  try {
    const raw = localStorage.getItem(HS_KEY);
    return raw ? JSON.parse(raw) : { score: 0, combo: 0, wave: 0 };
  } catch { return { score: 0, combo: 0, wave: 0 }; }
}

function saveLocalHighscore(score, combo, wave) {
  try {
    const prev = loadLocalHighscore();
    if (score > prev.score) {
      localStorage.setItem(HS_KEY, JSON.stringify({ score, combo, wave, date: new Date().toISOString() }));
      return true; // new record
    }
    return false;
  } catch { return false; }
}

// ── GLOBAL SERVER STUB ────────────────────────────────────────────────────────
// TODO: replace URL + body with your real API when ready.
// Signature is intentionally kept minimal so the rest of the app needs no changes.
async function submitScoreToServer(score, combo, wave) {
  /* Example implementation (uncomment + fill in when backend is ready):
  try {
    const res = await fetch('https://YOUR_API/scores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ score, combo, wave, ts: Date.now() }),
    });
    return res.ok;
  } catch { return false; }
  */
  console.log('[Highscore] Would submit to server:', { score, combo, wave });
  return false; // stub – always returns false until implemented
}

// ── ADMOB REVIVE STUBS ────────────────────────────────────────────────────────
// These two functions are the ONLY things your app-developer needs to replace
// when wiring up real AdMob Rewarded Ads via Capacitor / React Native.
//
// showRewardedAd(onRewarded)
//   → Load + show a rewarded ad. Call onRewarded() if the user earns the reward.
//   → On real AdMob: use AdMob.showRewardVideoAd() and fire onRewarded in the
//     'onRewardedVideoAdRewarded' listener.
//
// onAdRewarded  (passed as callback)
//   → Called automatically by showRewardedAd when the ad completes.
//   → Triggers the actual in-game revive logic — do NOT change its internals.
//
function showRewardedAd(onRewarded) {
  // ── STUB: simulates a 3-second rewarded ad ──────────────────────────────
  // Replace the body of this function with real AdMob SDK calls.
  // Keep the onRewarded() call so the game logic stays unchanged.
  console.log('[AdMob] Showing simulated rewarded ad…');
  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('[AdMob] Ad completed – granting reward.');
      onRewarded();
      resolve();
    }, 3000);
  });
  // ── Real AdMob example (Capacitor plugin) ───────────────────────────────
  // import { AdMob, RewardAdPluginEvents } from '@capacitor-community/admob';
  // await AdMob.prepareRewardVideoAd({ adId: 'ca-app-pub-XXXX/YYYY' });
  // AdMob.addListener(RewardAdPluginEvents.Rewarded, () => { onRewarded(); resolve(); });
  // await AdMob.showRewardVideoAd();
}

function makeParticles(x, y, color, count = 8, sp = 1, size = 3.5, life = 0.7) {
  return Array.from({ length: count }, () => {
    const a = Math.random() * Math.PI * 2, s = rand(1.5, 5) * sp;
    return { x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, life: rand(0.35, life), maxLife: life, color, size: size * rand(0.6, 1.4) };
  });
}

function makeRing(x, y, color, maxR = 40) {
  return { x, y, color, r: 4, maxR, life: 1, maxLife: 1, isRing: true };
}

function makeLightning(x1, y1, x2, y2, color, segs = 9) {
  const pts = [{ x: x1, y: y1 }];
  for (let i = 1; i < segs; i++) {
    const t = i / segs;
    pts.push({ x: lerp(x1, x2, t) + (Math.random() - 0.5) * 20, y: lerp(y1, y2, t) + (Math.random() - 0.5) * 20 });
  }
  pts.push({ x: x2, y: y2 });
  return { pts, color, life: 0.13, maxLife: 0.13, isLightning: true };
}

function initState() {
  return {
    player: {
      x: W / 2, y: H / 2, hp: 100, maxHp: 100, angle: -Math.PI / 2,
      speed: 4.8, radius: 13,
      fireTimer: 0, fireRate: 0.14,
      dashCd: 0, dashDur: 0, dashVx: 0, dashVy: 0,
      invincible: 0, trailPts: [], tripleShot: 0,
    },
    bullets: [], enemies: [], particles: [], powerups: [],
    combo: 0, comboTimer: 0, comboWindow: 2.2, prevCombo: 0,
    score: 0, overdriveEnergy: 0, overdriveActive: false, overdriveDur: 0,
    wave: 1, waveTimer: 0, spawnTimer: 0, spawnInterval: 1.6,
    burstQueue: 0, burstTimer: 0, time: 0, bossActive: false,
    input: { dx: 0, dy: 0 },
    shake: { x: 0, y: 0, power: 0, dur: 0 }, glitch: 0, totalKills: 0,
    bgStars: Array.from({ length: 50 }, () => ({
      x: rand(MARGIN, W - MARGIN), y: rand(MARGIN, H - MARGIN),
      r: rand(0.5, 2.2), pulse: Math.random() * Math.PI * 2, speed: rand(0.4, 2.2),
    })),
  };
}

/* ═══════════════════════════════════════════════════════════
   UPDATE
═══════════════════════════════════════════════════════════ */
function update(s, dt, audio) {
  s.time += dt;
  const p = s.player;

  // Music intensity driven by gameplay
  audio.setIntensity(clamp(s.combo / 40 + s.time / 200 + (s.overdriveActive ? 0.65 : 0), 0, 1));

  // Player trail
  p.trailPts.unshift({ x: p.x, y: p.y, t: 0.22 });
  p.trailPts = p.trailPts.filter(pt => { pt.t -= dt; return pt.t > 0; }).slice(0, 20);

  // Power-up timers
  if (p.tripleShot > 0) p.tripleShot -= dt;

  // Movement
  if (p.dashDur > 0) {
    p.dashDur -= dt;
    p.x += p.dashVx * 400 * dt;
    p.y += p.dashVy * 400 * dt;
  } else {
    const sp = p.speed * (s.overdriveActive ? 1.28 : 1) * (1 + Math.min(s.combo / 5, 10) * 0.07);
    p.x += s.input.dx * sp * 60 * dt;
    p.y += s.input.dy * sp * 60 * dt;
  }
  p.x = clamp(p.x, MARGIN + p.radius, W - MARGIN - p.radius);
  p.y = clamp(p.y, MARGIN + p.radius, H - MARGIN - p.radius);
  if (p.dashCd > 0) p.dashCd -= dt;
  if (p.invincible > 0) p.invincible -= dt;

  // Auto-aim + shoot
  p.fireTimer -= dt;
  const fi = s.overdriveActive ? p.fireRate * 0.5 : p.fireRate;
  if (p.fireTimer <= 0 && s.enemies.length > 0) {
    let nearest = null, nd = Infinity;
    for (const e of s.enemies) { const d = dist(p, e); if (d < nd) { nd = d; nearest = e; } }
    if (nearest) {
      p.fireTimer = fi;
      const dir = norm({ x: nearest.x - p.x, y: nearest.y - p.y });
      p.angle = Math.atan2(dir.y, dir.x);
      const da = (Math.random() - 0.5) * 0.06;
      const c = Math.cos(da), sn = Math.sin(da);
      const bvx = (dir.x * c - dir.y * sn) * 20, bvy = (dir.x * sn + dir.y * c) * 20;
      const dmg = s.overdriveActive ? 12 * 1.85 : 12;
      const col = s.overdriveActive ? '#ffdd00' : '#ff7b35';
      s.bullets.push({ x: p.x, y: p.y, vx: bvx, vy: bvy, life: 2.4, damage: dmg, fromPlayer: true, col, trail: [] });
      audio.shoot();
      // Triple Shot power-up: two extra angled bullets
      if (p.tripleShot > 0) {
        const spread = 0.28;
        for (const spr of [-spread, spread]) {
          const cs = Math.cos(spr), ss = Math.sin(spr);
          const tvx = bvx * cs - bvy * ss, tvy = bvx * ss + bvy * cs;
          s.bullets.push({ x: p.x, y: p.y, vx: tvx, vy: tvy, life: 2.4, damage: dmg * 0.75, fromPlayer: true, col: '#a259ff', trail: [] });
        }
      }
      if (s.combo >= 20 || s.overdriveActive) {
        const off = { x: -dir.y * 7, y: dir.x * 7 };
        s.bullets.push({ x: p.x + off.x, y: p.y + off.y, vx: bvx, vy: bvy, life: 2.4, damage: dmg * 0.7, fromPlayer: true, col, trail: [] });
      }
    }
  }

  // Bullets
  for (let i = s.bullets.length - 1; i >= 0; i--) {
    const b = s.bullets[i];
    b.trail.unshift({ x: b.x, y: b.y });
    if (b.trail.length > 9) b.trail.pop();
    b.x += b.vx * 60 * dt; b.y += b.vy * 60 * dt; b.life -= dt;
    if (b.life <= 0 || b.x < -20 || b.x > W + 20 || b.y < -20 || b.y > H + 20) { s.bullets.splice(i, 1); continue; }
    if (b.fromPlayer) {
      for (let j = s.enemies.length - 1; j >= 0; j--) {
        const e = s.enemies[j];
        if (dist(b, e) < e.radius + 5) {
          e.hp -= b.damage; e.flashTimer = 0.11;
          s.bullets.splice(i, 1);
          audio.hit(e.isElite || e.isBoss);
          s.particles.push(...makeParticles(e.x, e.y, e.isBoss ? '#ff6600' : e.isElite ? '#ff00ff' : e.isOrbiter ? '#00ffcc' : '#ff6b35', 5, 0.85));
          if (e.hp <= 0) {
            s.combo++;
            if ((s.combo === 15) || (s.combo === 30) || (s.combo === 60)) audio.comboMilestone(s.combo);
            s.prevCombo = s.combo;
            s.comboTimer = s.comboWindow; s.totalKills++;
            const cMult = 1 + clamp(s.combo * 0.05, 0, 4);
            const oMult = s.overdriveActive ? 1.5 : 1;
            const surv = 1 + Math.floor(s.time / 60) * 0.08;
            const pts = (e.isBoss ? 2500 : e.isElite ? 300 : e.isOrbiter ? 175 : 100) * cMult * oMult * surv;
            s.score += pts;
            s.overdriveEnergy = Math.min(1, s.overdriveEnergy + (e.isBoss ? 0.6 : e.isElite ? 0.18 : e.isOrbiter ? 0.12 : 0.08));
            if (e.isBoss) s.bossActive = false;
            // Power-up drop
            const dropType = e.isBoss ? 'boss' : e.isElite ? 'elite' : e.isOrbiter ? 'orbiter' : 'normal';
            if (Math.random() < POWERUP_DROP_CHANCE[dropType]) {
              s.powerups.push(makePowerup(e.x, e.y));
            }
            audio.die(e.isElite || e.isBoss);
            s.particles.push(
              ...makeParticles(e.x, e.y, e.isBoss ? '#ff6600' : e.isElite ? '#ff00ff' : e.isOrbiter ? '#00ffcc' : '#ff6b35', e.isBoss ? 40 : e.isElite ? 22 : 13, e.isBoss ? 2.0 : 1.4, e.isBoss ? 7 : 5, 0.8),
              ...makeParticles(e.x, e.y, '#ffffff', e.isBoss ? 14 : e.isElite ? 8 : 4, 0.6, 2, 0.4),
              makeRing(e.x, e.y, e.isBoss ? '#ff6600' : e.isElite ? '#ff00ff' : e.isOrbiter ? '#00ffcc' : '#ff6b35', e.isBoss ? 120 : e.isElite ? 55 : 35),
            );
            if (e.isElite || e.isBoss) {
              s.particles.push(makeRing(e.x, e.y, '#ffffff', e.isBoss ? 200 : 70));
              s.glitch = e.isBoss ? 0.9 : 0.45;
            }
            s.shake = { x: 0, y: 0, power: e.isBoss ? 22 : e.isElite ? 11 : 5.5, dur: e.isBoss ? 0.45 : e.isElite ? 0.22 : 0.11 };
            if (e.isBoss) {
              s.particles.push({ x: W / 2, y: H / 2 - 70, vx: 0, vy: -0.3, life: 2.2, maxLife: 2.2, color: '#ff6600', size: 0, isText: true, text: `⚡ BOSS DOWN! ⚡` });
            }
            // Overdrive chain lightning
            if (s.overdriveActive) {
              s.enemies.filter(en => en !== e && dist(e, en) < 110).slice(0, 3)
                .forEach(en => s.particles.push(makeLightning(e.x, e.y, en.x, en.y, '#ffdd00')));
            }
            s.particles.push({ x: e.x, y: e.y - 14, vx: 0, vy: -0.9, life: 0.9, maxLife: 0.9, color: e.isBoss ? '#ff6600' : e.isElite ? '#ff00ff' : e.isOrbiter ? '#00ffcc' : '#ffdd00', size: 0, isText: true, text: `+${Math.floor(pts)}` });
            s.enemies.splice(j, 1);
          }
          break;
        }
      }
    } else {
      if (p.invincible <= 0 && dist(b, p) < p.radius + 5) {
        p.hp -= b.damage; p.invincible = 0.65;
        s.bullets.splice(i, 1);
        s.shake = { x: 0, y: 0, power: 14, dur: 0.26 };
        audio.playerHurt();
        if (s.combo > 5) { audio.comboBreak(); s.particles.push({ x: W / 2, y: H / 2 - 75, vx: 0, vy: -0.5, life: 0.75, maxLife: 0.75, color: '#ff3366', size: 0, isText: true, text: 'COMBO BREAK' }); }
        s.combo = 0; s.comboTimer = 0; s.prevCombo = 0;
      }
    }
  }

  // Enemies
  for (const e of s.enemies) {
    if (e.spawnPop > 0) { e.spawnPop -= dt * 3; continue; }

    if (e.isOrbiter) {
      // Pivot slowly drifts toward player
      const pivDir = norm({ x: p.x - e.pivotX, y: p.y - e.pivotY });
      e.pivotX += pivDir.x * e.speed * 28 * dt;
      e.pivotY += pivDir.y * e.speed * 28 * dt;
      e.pivotX = clamp(e.pivotX, MARGIN + e.radius + e.orbitRadius, W - MARGIN - e.radius - e.orbitRadius);
      e.pivotY = clamp(e.pivotY, MARGIN + e.radius + e.orbitRadius, H - MARGIN - e.radius - e.orbitRadius);
      // Orbit around pivot
      e.orbitAngle += e.orbitSpeed * dt;
      e.x = e.pivotX + Math.cos(e.orbitAngle) * e.orbitRadius;
      e.y = e.pivotY + Math.sin(e.orbitAngle) * e.orbitRadius;
      e.angle = e.orbitAngle + Math.PI / 2;
    } else if (e.isBoss) {
      // Boss: sinusoidal approach + strafe
      const dir = norm({ x: p.x - e.x, y: p.y - e.y });
      const strafeA = e.orbitAngle;
      const strafeX = Math.cos(strafeA + Math.PI / 2) * 0.65;
      const strafeY = Math.sin(strafeA + Math.PI / 2) * 0.65;
      e.vx = lerp(e.vx, (dir.x + strafeX) * e.speed, 0.04);
      e.vy = lerp(e.vy, (dir.y + strafeY) * e.speed, 0.04);
      e.x += e.vx * 60 * dt; e.y += e.vy * 60 * dt;
      e.orbitAngle += dt * 0.9;
      e.angle = Math.atan2(dir.y, dir.x);
    } else {
      const dir = norm({ x: p.x - e.x, y: p.y - e.y });
      e.vx = lerp(e.vx, dir.x * e.speed, 0.09); e.vy = lerp(e.vy, dir.y * e.speed, 0.09);
      e.x += e.vx * 60 * dt; e.y += e.vy * 60 * dt;
      e.angle = Math.atan2(dir.y, dir.x);
    }

    if (!e.isOrbiter && !e.isBoss) e.orbitAngle += dt * (e.isElite ? 1.6 : 2.4);
    if (e.flashTimer > 0) e.flashTimer -= dt;
    e.x = clamp(e.x, MARGIN + e.radius, W - MARGIN - e.radius);
    e.y = clamp(e.y, MARGIN + e.radius, H - MARGIN - e.radius);

    if (e.isElite) {
      e.shootTimer -= dt;
      if (e.shootTimer <= 0) {
        e.shootTimer = clamp(2.6 - s.wave * 0.11, 0.75, 2.6);
        const sd = norm({ x: p.x - e.x, y: p.y - e.y });
        s.bullets.push({ x: e.x, y: e.y, vx: sd.x * 11, vy: sd.y * 11, life: 3, damage: 17, fromPlayer: false, col: '#ff0055', trail: [] });
        if (s.wave >= 8) {
          const perp = { x: -sd.y * 0.28, y: sd.x * 0.28 };
          s.bullets.push({ x: e.x, y: e.y, vx: (sd.x + perp.x) * 9, vy: (sd.y + perp.y) * 9, life: 3, damage: 12, fromPlayer: false, col: '#ff0055', trail: [] });
          s.bullets.push({ x: e.x, y: e.y, vx: (sd.x - perp.x) * 9, vy: (sd.y - perp.y) * 9, life: 3, damage: 12, fromPlayer: false, col: '#ff0055', trail: [] });
        }
      }
    }

    if (e.isBoss) {
      e.shootTimer -= dt;
      if (e.shootTimer <= 0) {
        e.shootPhase = (e.shootPhase + 1) % 3;
        const sd = norm({ x: p.x - e.x, y: p.y - e.y });
        const baseA = Math.atan2(sd.y, sd.x);
        if (e.shootPhase === 0) {
          // Spread shot: 5 bullets
          for (let i = -2; i <= 2; i++) {
            const a = baseA + i * 0.22;
            s.bullets.push({ x: e.x, y: e.y, vx: Math.cos(a) * 10, vy: Math.sin(a) * 10, life: 3, damage: 14, fromPlayer: false, col: '#ff6600', trail: [] });
          }
          e.shootTimer = 1.2;
        } else if (e.shootPhase === 1) {
          // Ring burst: 8 bullets in all directions
          for (let i = 0; i < 8; i++) {
            const a = (i / 8) * Math.PI * 2;
            s.bullets.push({ x: e.x, y: e.y, vx: Math.cos(a) * 8, vy: Math.sin(a) * 8, life: 3.5, damage: 12, fromPlayer: false, col: '#ff3300', trail: [] });
          }
          e.shootTimer = 1.6;
        } else {
          // Aimed triple burst
          for (let i = -1; i <= 1; i++) {
            const a = baseA + i * 0.12;
            s.bullets.push({ x: e.x, y: e.y, vx: Math.cos(a) * 13, vy: Math.sin(a) * 13, life: 2.5, damage: 18, fromPlayer: false, col: '#ff0000', trail: [] });
          }
          e.shootTimer = 0.9;
        }
      }
    }

    if (p.invincible <= 0 && dist(e, p) < e.radius + p.radius) {
      p.hp -= e.isBoss ? 30 : e.isElite ? 22 : 14; p.invincible = 0.8;
      s.shake = { x: 0, y: 0, power: e.isBoss ? 22 : 16, dur: 0.3 };
      audio.playerHurt();
      const kd = norm({ x: p.x - e.x, y: p.y - e.y });
      p.x += kd.x * (e.isBoss ? 40 : 28); p.y += kd.y * (e.isBoss ? 40 : 28);
      if (s.combo > 5) { audio.comboBreak(); }
      s.combo = 0; s.comboTimer = 0; s.prevCombo = 0;
    }
  }

  if (s.combo > 0) { s.comboTimer -= dt; if (s.comboTimer <= 0) { s.combo = 0; s.prevCombo = 0; } }

  if (s.overdriveActive) {
    s.overdriveDur -= dt;
    if (s.overdriveDur <= 0) {
      s.overdriveActive = false; s.glitch = 0.75;
      s.shake = { x: 0, y: 0, power: 10, dur: 0.36 };
      s.particles.push(makeRing(p.x, p.y, '#ffdd00', 140));
    }
  }

  const scale = 1 + s.combo * 0.05 + s.wave * 0.14 + s.time / 90;
  s.spawnTimer -= dt;
  if (s.spawnTimer <= 0) {
    s.spawnTimer = clamp(s.spawnInterval / scale, 0.22, s.spawnInterval);
    if (s.enemies.length < clamp(14 + s.wave * 8, 14, 90)) {
      s.enemies.push(makeEnemy(s.wave));
      if (Math.random() < 0.07 + s.wave * 0.01) { s.burstQueue += 3; s.burstTimer = 0; }
    }
  }
  if (s.burstQueue > 0) { s.burstTimer -= dt; if (s.burstTimer <= 0) { s.burstTimer = 0.27; s.burstQueue--; s.enemies.push(makeEnemy(s.wave)); } }

  s.waveTimer += dt;
  if (s.waveTimer > 26 + s.wave * 4) {
    s.waveTimer = 0; s.wave++;
    s.spawnInterval = clamp(s.spawnInterval * 0.87, 0.38, 2);
    s.glitch = 0.7; audio.waveUp(s.wave);
    s.particles.push({ x: W / 2, y: H / 2 - 90, vx: 0, vy: -0.35, life: 1.4, maxLife: 1.4, color: '#00f5ff', size: 0, isText: true, text: `— WAVE ${s.wave} —` });
    // Boss every 10 waves
    if (s.wave % 10 === 0 && !s.bossActive) {
      s.bossActive = true;
      s.enemies.push(makeBoss(s.wave));
      s.glitch = 1.2;
      s.shake = { x: 0, y: 0, power: 18, dur: 0.6 };
      s.particles.push({ x: W / 2, y: H / 2 - 110, vx: 0, vy: -0.3, life: 2.0, maxLife: 2.0, color: '#ff6600', size: 0, isText: true, text: `⚡ BOSS INCOMING! ⚡` });
    }
  }

  // Power-ups
  for (let i = s.powerups.length - 1; i >= 0; i--) {
    const pu = s.powerups[i];
    pu.y += pu.vy * 60 * dt;
    pu.pulseAngle += dt * 4;
    pu.life -= dt;
    if (pu.life <= 0) { s.powerups.splice(i, 1); continue; }
    // Pickup
    if (dist(pu, p) < pu.radius + p.radius) {
      p.tripleShot = TRIPLE_SHOT_DURATION;
      s.powerups.splice(i, 1);
      s.particles.push(
        ...makeParticles(p.x, p.y, '#a259ff', 18, 1.1, 4.5, 0.7),
        makeRing(p.x, p.y, '#a259ff', 55),
        { x: W / 2, y: H / 2 - 90, vx: 0, vy: -0.3, life: 1.4, maxLife: 1.4, color: '#a259ff', size: 0, isText: true, text: '✦ TRIPLE SHOT ✦' },
      );
    }
  }

  for (let i = s.particles.length - 1; i >= 0; i--) {
    const pt = s.particles[i];
    if (pt.isRing) { pt.r = lerp(pt.r, pt.maxR, 0.13); pt.life -= dt; if (pt.life <= 0) s.particles.splice(i, 1); continue; }
    if (pt.isLightning) { pt.life -= dt; if (pt.life <= 0) s.particles.splice(i, 1); continue; }
    pt.x += pt.vx * 60 * dt; pt.y += pt.vy * 60 * dt;
    pt.vx *= 0.92; pt.vy *= 0.92;
    pt.life -= dt; if (pt.life <= 0) s.particles.splice(i, 1);
  }

  if (s.shake.dur > 0) { s.shake.dur -= dt; s.shake.x = (Math.random() - 0.5) * s.shake.power; s.shake.y = (Math.random() - 0.5) * s.shake.power; }
  else { s.shake.x = 0; s.shake.y = 0; }
  if (s.glitch > 0) s.glitch -= dt;
  p.hp = clamp(p.hp, 0, p.maxHp);
}

/* ═══════════════════════════════════════════════════════════
   RENDER  –  dual-canvas pipeline (main + off-screen glow)
═══════════════════════════════════════════════════════════ */
function render(ctx, glowCtx, s, t) {
  const { x: sx, y: sy } = s.shake;

  // ── OFF-SCREEN GLOW PASS ─────────────────────────────
  glowCtx.clearRect(0, 0, W, H);
  glowCtx.save();
  glowCtx.translate(sx, sy);

  for (const b of s.bullets) {
    if (!b.fromPlayer) continue;
    glowCtx.fillStyle = b.col || '#ff7b35';
    glowCtx.shadowColor = glowCtx.fillStyle; glowCtx.shadowBlur = 22;
    glowCtx.beginPath(); glowCtx.arc(b.x, b.y, 7, 0, Math.PI * 2); glowCtx.fill();
  }
  if (s.overdriveActive) {
    const p2 = s.player;
    const g = glowCtx.createRadialGradient(p2.x, p2.y, 0, p2.x, p2.y, 90);
    const pulse = 0.55 + Math.sin(t * 9) * 0.45;
    g.addColorStop(0, `rgba(255,220,0,${0.25 * pulse})`);
    g.addColorStop(1, 'rgba(255,80,0,0)');
    glowCtx.fillStyle = g;
    glowCtx.beginPath(); glowCtx.arc(p2.x, p2.y, 90, 0, Math.PI * 2); glowCtx.fill();
  }
  glowCtx.shadowBlur = 0;
  glowCtx.restore();

  // ── MAIN CANVAS ──────────────────────────────────────
  ctx.fillStyle = '#03030e'; ctx.fillRect(0, 0, W, H);
  ctx.save(); ctx.translate(sx, sy);

  // Animated background stars
  for (const st of s.bgStars) {
    st.pulse += st.speed * 0.018;
    const br = 0.28 + 0.28 * Math.sin(st.pulse);
    ctx.fillStyle = s.overdriveActive ? `rgba(255,180,0,${br * 0.5})` : `rgba(100,180,255,${br * 0.4})`;
    ctx.beginPath(); ctx.arc(st.x, st.y, st.r, 0, Math.PI * 2); ctx.fill();
  }

  // Grid
  ctx.strokeStyle = s.overdriveActive ? 'rgba(255,180,0,0.06)' : 'rgba(40,90,220,0.07)';
  ctx.lineWidth = 0.6;
  const gs = 40, gOff = (t * 8) % gs;
  for (let x = -gs + gOff % gs; x < W + gs; x += gs) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, H); ctx.stroke(); }
  for (let y = -gs + gOff % gs; y < H + gs; y += gs) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(W, y); ctx.stroke(); }

  // Arena border
  const bc = s.overdriveActive ? '#ffdd00' : s.glitch > 0.15 ? `hsl(${300 + s.glitch * 60},100%,60%)` : '#00f5ff';
  ctx.strokeStyle = bc; ctx.lineWidth = s.overdriveActive ? 2.5 : 1.8;
  ctx.shadowColor = bc; ctx.shadowBlur = s.overdriveActive ? 32 : 18;
  ctx.strokeRect(MARGIN, MARGIN, W - 2 * MARGIN, H - 2 * MARGIN);
  const cLen = 16;
  for (const [cx, cy] of [[MARGIN, MARGIN], [W - MARGIN, MARGIN], [MARGIN, H - MARGIN], [W - MARGIN, H - MARGIN]]) {
    const dx = cx === MARGIN ? 1 : -1, dy = cy === MARGIN ? 1 : -1;
    ctx.beginPath(); ctx.moveTo(cx, cy + dy * cLen); ctx.lineTo(cx, cy); ctx.lineTo(cx + dx * cLen, cy); ctx.stroke();
  }
  ctx.shadowBlur = 0;

  // Glitch bars
  if (s.glitch > 0) {
    ctx.save(); ctx.globalAlpha = s.glitch * 0.22;
    ctx.fillStyle = s.overdriveActive ? '#ffcc00' : '#ff00ff';
    for (let i = 0; i < 5; i++) ctx.fillRect(Math.random() * 30 - 15, Math.random() * H, W + 30, rand(1.5, 9));
    ctx.restore();
  }

  // Overdrive vignette
  if (s.overdriveActive) {
    const g = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.78);
    g.addColorStop(0, 'rgba(255,200,0,0.04)'); g.addColorStop(1, 'rgba(255,80,0,0.1)');
    ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
  }

  // Rings
  for (const pt of s.particles) {
    if (!pt.isRing) continue;
    const a = clamp(pt.life / pt.maxLife, 0, 1);
    ctx.globalAlpha = a * 0.7; ctx.strokeStyle = pt.color;
    ctx.lineWidth = 3 * a; ctx.shadowColor = pt.color; ctx.shadowBlur = 14;
    ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.r, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.shadowBlur = 0; ctx.globalAlpha = 1;

  // Lightning
  for (const pt of s.particles) {
    if (!pt.isLightning) continue;
    const a = clamp(pt.life / pt.maxLife, 0, 1);
    ctx.globalAlpha = a; ctx.strokeStyle = pt.color; ctx.lineWidth = 2.5 * a;
    ctx.shadowColor = pt.color; ctx.shadowBlur = 20;
    ctx.beginPath(); ctx.moveTo(pt.pts[0].x, pt.pts[0].y);
    for (let i = 1; i < pt.pts.length; i++) ctx.lineTo(pt.pts[i].x, pt.pts[i].y);
    ctx.stroke();
  }
  ctx.shadowBlur = 0; ctx.globalAlpha = 1;

  // Regular particles
  for (const pt of s.particles) {
    if (pt.isText || pt.isRing || pt.isLightning) continue;
    const a = clamp(pt.life / pt.maxLife, 0, 1);
    ctx.globalAlpha = a; ctx.fillStyle = pt.color;
    ctx.shadowColor = pt.color; ctx.shadowBlur = 7;
    ctx.beginPath(); ctx.arc(pt.x, pt.y, pt.size * a, 0, Math.PI * 2); ctx.fill();
  }
  ctx.shadowBlur = 0; ctx.globalAlpha = 1;

  // Text particles
  for (const pt of s.particles) {
    if (!pt.isText) continue;
    const a = clamp(pt.life / pt.maxLife, 0, 1);
    const big = pt.text.startsWith('—') || pt.text.startsWith('⚡');
    ctx.globalAlpha = a; ctx.font = `bold ${big ? 20 : 12}px 'Courier New',monospace`;
    ctx.fillStyle = pt.color; ctx.textAlign = 'center';
    ctx.shadowColor = pt.color; ctx.shadowBlur = 13;
    ctx.fillText(pt.text, pt.x, pt.y);
    ctx.shadowBlur = 0; ctx.textAlign = 'left'; ctx.globalAlpha = 1;
  }

  // Enemy bullets
  for (const b of s.bullets) {
    if (b.fromPlayer) continue;
    for (let i = 0; i < b.trail.length; i++) {
      ctx.globalAlpha = (1 - i / b.trail.length) * 0.22;
      ctx.fillStyle = '#ff0055';
      ctx.beginPath(); ctx.arc(b.trail[i].x, b.trail[i].y, 3.5 * (1 - i / b.trail.length), 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = '#ff0055'; ctx.shadowColor = '#ff2266'; ctx.shadowBlur = 13;
    ctx.beginPath(); ctx.arc(b.x, b.y, 5.5, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
  }

  // Player bullets
  for (const b of s.bullets) {
    if (!b.fromPlayer) continue;
    const col = b.col || '#ff7b35';
    for (let i = 0; i < b.trail.length; i++) {
      ctx.globalAlpha = (1 - i / b.trail.length) * 0.2;
      ctx.fillStyle = col;
      ctx.beginPath(); ctx.arc(b.trail[i].x, b.trail[i].y, 4.5 * (1 - i / b.trail.length), 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.fillStyle = col; ctx.shadowColor = col; ctx.shadowBlur = s.overdriveActive ? 18 : 10;
    ctx.beginPath(); ctx.arc(b.x, b.y, s.overdriveActive ? 5.5 : 4, 0, Math.PI * 2); ctx.fill();
    ctx.shadowBlur = 0;
  }

  // Enemies
  for (const e of s.enemies) {
    ctx.save(); ctx.translate(e.x, e.y);
    const pop = e.spawnPop > 0 ? clamp(1 - e.spawnPop / 0.3, 0.05, 1) : 1;
    ctx.scale(pop, pop);

    if (e.isBoss) {
      // Boss: pulsing orange hexagon with rotating rings
      const pulse = 0.85 + Math.sin(t * 3.5) * 0.15;
      const col = e.flashTimer > 0 ? '#ffffff' : '#ff6600';
      ctx.shadowColor = col; ctx.shadowBlur = 36 * pulse; ctx.fillStyle = col;
      // Outer rotating ring
      ctx.save();
      ctx.rotate(t * 0.9);
      ctx.strokeStyle = e.flashTimer > 0 ? '#ffffff' : '#ff4400';
      ctx.lineWidth = 4; ctx.shadowBlur = 18;
      ctx.beginPath(); ctx.arc(0, 0, e.radius + 12, 0, Math.PI * 2); ctx.stroke();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        ctx.fillStyle = e.flashTimer > 0 ? '#ffffff' : '#ffaa00';
        ctx.beginPath(); ctx.arc(Math.cos(a) * (e.radius + 12), Math.sin(a) * (e.radius + 12), 4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      // Inner counter-rotating ring
      ctx.save();
      ctx.rotate(-t * 1.6);
      ctx.strokeStyle = e.flashTimer > 0 ? '#ffffff' : '#ff2200';
      ctx.lineWidth = 2; ctx.shadowBlur = 10;
      ctx.setLineDash([8, 8]);
      ctx.beginPath(); ctx.arc(0, 0, e.radius * 0.72, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.restore();
      // Core hexagon
      ctx.rotate(e.angle + t * 0.6);
      ctx.fillStyle = col; ctx.shadowBlur = 28;
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2 - Math.PI / 6;
        i === 0 ? ctx.moveTo(Math.cos(a) * e.radius, Math.sin(a) * e.radius) : ctx.lineTo(Math.cos(a) * e.radius, Math.sin(a) * e.radius);
      }
      ctx.closePath(); ctx.fill();
      // Core glow
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = '#ffffff';
      ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(0, 0, e.radius * 0.3, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    } else if (e.isOrbiter) {
      // Orbiter: cyan circle with spinning dashes
      ctx.rotate(e.angle);
      const col = e.flashTimer > 0 ? '#ffffff' : '#00ffcc';
      ctx.shadowColor = col; ctx.shadowBlur = 18; ctx.strokeStyle = col;
      ctx.lineWidth = 2.5;
      ctx.beginPath(); ctx.arc(0, 0, e.radius, 0, Math.PI * 2); ctx.stroke();
      // Spinning inner cross
      ctx.rotate(t * 3.2);
      ctx.fillStyle = col;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        ctx.beginPath(); ctx.arc(Math.cos(a) * e.radius * 0.55, Math.sin(a) * e.radius * 0.55, 3, 0, Math.PI * 2); ctx.fill();
      }
      ctx.beginPath(); ctx.arc(0, 0, e.radius * 0.28, 0, Math.PI * 2); ctx.fill();
    } else if (e.isElite) {
      // Orbit particles
      for (let r = 0; r < 3; r++) {
        const oa = e.orbitAngle + (r * Math.PI * 2 / 3);
        const ox = Math.cos(oa) * e.radius * 1.6, oy = Math.sin(oa) * e.radius * 0.65;
        const pc = e.flashTimer > 0 ? '#ffffff' : 'rgba(255,0,200,0.8)';
        ctx.fillStyle = pc; ctx.shadowColor = '#ff00dd'; ctx.shadowBlur = 10;
        ctx.beginPath(); ctx.arc(ox, oy, 3.5, 0, Math.PI * 2); ctx.fill();
      }
      ctx.rotate(e.angle + Math.PI / 4);
      const col = e.flashTimer > 0 ? '#ffffff' : '#ff00dd';
      ctx.shadowColor = col; ctx.shadowBlur = 26; ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(0, -e.radius); ctx.lineTo(e.radius, 0); ctx.lineTo(0, e.radius); ctx.lineTo(-e.radius, 0);
      ctx.closePath(); ctx.fill();
      ctx.rotate(-e.angle - Math.PI / 4 + t * 2.8);
      ctx.globalAlpha = 0.55; ctx.fillStyle = '#ff88ff';
      ctx.shadowBlur = 0;
      ctx.beginPath(); ctx.arc(0, 0, e.radius * 0.45, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    } else {
      ctx.rotate(e.angle);
      const col = e.flashTimer > 0 ? '#ffffff' : '#ff2244';
      ctx.shadowColor = col; ctx.shadowBlur = 14; ctx.fillStyle = col;
      ctx.beginPath();
      ctx.moveTo(e.radius * 0.95, 0);
      ctx.lineTo(-e.radius * 0.75, -e.radius * 0.72);
      ctx.lineTo(-e.radius * 0.38, 0);
      ctx.lineTo(-e.radius * 0.75, e.radius * 0.72);
      ctx.closePath(); ctx.fill();
    }
    ctx.restore(); ctx.shadowBlur = 0;

    // HP bars for elites, orbiters, and boss
    if ((e.isElite || e.isOrbiter || e.isBoss) && e.hp < e.maxHp) {
      const bw = e.isBoss ? e.radius * 5 : e.radius * 3, bh = e.isBoss ? 7 : 5;
      ctx.fillStyle = 'rgba(0,0,0,0.68)'; ctx.fillRect(e.x - bw / 2, e.y - e.radius - (e.isBoss ? 22 : 14), bw, bh);
      const hpFr = e.hp / e.maxHp;
      ctx.fillStyle = e.isBoss ? (hpFr > 0.5 ? '#ff6600' : '#ff2200') : e.isOrbiter ? '#00ffcc' : hpFr > 0.5 ? '#ff00dd' : '#ff0066';
      ctx.shadowColor = ctx.fillStyle; ctx.shadowBlur = 4;
      ctx.fillRect(e.x - bw / 2, e.y - e.radius - (e.isBoss ? 22 : 14), bw * hpFr, bh);
      ctx.shadowBlur = 0;
      if (e.isBoss) {
        ctx.font = `bold 11px 'Courier New',monospace`; ctx.textAlign = 'center';
        ctx.fillStyle = '#ff6600'; ctx.fillText('BOSS', e.x, e.y - e.radius - 27);
        ctx.textAlign = 'left';
      }
    }
  }

  // Power-ups
  for (const pu of s.powerups) {
    const pulse = 0.75 + Math.sin(pu.pulseAngle) * 0.25;
    const lifeAlpha = clamp(pu.life < 3 ? pu.life / 3 : 1, 0.1, 1); // blink out when expiring
    ctx.globalAlpha = pulse * lifeAlpha;
    // Outer glow ring
    ctx.strokeStyle = '#a259ff'; ctx.shadowColor = '#a259ff'; ctx.shadowBlur = 18;
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(pu.x, pu.y, pu.radius * (1 + 0.15 * Math.sin(pu.pulseAngle * 1.5)), 0, Math.PI * 2); ctx.stroke();
    // Filled core
    ctx.fillStyle = 'rgba(162,89,255,0.22)';
    ctx.beginPath(); ctx.arc(pu.x, pu.y, pu.radius, 0, Math.PI * 2); ctx.fill();
    // Icon: three bullet lines
    ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.8; ctx.shadowBlur = 8;
    for (let i = -1; i <= 1; i++) {
      ctx.beginPath();
      ctx.moveTo(pu.x + i * 5, pu.y + 5);
      ctx.lineTo(pu.x + i * 5, pu.y - 5);
      ctx.stroke();
    }
    ctx.shadowBlur = 0; ctx.globalAlpha = 1;
  }

  // Player trail
  const p = s.player;
  for (let i = 0; i < p.trailPts.length; i++) {
    const pt = p.trailPts[i];
    const a = clamp(pt.t / 0.22, 0, 1) * 0.35;
    ctx.globalAlpha = a;
    const tc = s.overdriveActive ? '#ff8800' : '#0088ff';
    ctx.fillStyle = tc; ctx.shadowColor = tc; ctx.shadowBlur = 7;
    ctx.beginPath(); ctx.arc(pt.x, pt.y, p.radius * 0.4 * (1 - i / 20), 0, Math.PI * 2); ctx.fill();
  }
  ctx.shadowBlur = 0; ctx.globalAlpha = 1;

  // Player ship
  const showP = p.invincible <= 0 || Math.floor(t * 12) % 2 === 0;
  if (showP) {
    ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.angle);
    const pc = s.overdriveActive ? '#ffdd00' : '#00f5ff';
    ctx.shadowColor = pc; ctx.shadowBlur = s.overdriveActive ? 40 : 24;

    // Engine flame
    const moving = s.input.dx !== 0 || s.input.dy !== 0 || p.dashDur > 0;
    if (moving) {
      const fl = p.radius * 2 + Math.random() * p.radius;
      const fc = s.overdriveActive ? '#ff8800' : '#004cff';
      ctx.globalAlpha = 0.55; ctx.fillStyle = fc;
      ctx.beginPath(); ctx.moveTo(-p.radius * 0.55, -p.radius * 0.32); ctx.lineTo(-fl, 0); ctx.lineTo(-p.radius * 0.55, p.radius * 0.32); ctx.closePath(); ctx.fill();
      ctx.globalAlpha = 0.8; ctx.fillStyle = '#ffffff';
      ctx.beginPath(); ctx.moveTo(-p.radius * 0.55, -p.radius * 0.14); ctx.lineTo(-fl * 0.52, 0); ctx.lineTo(-p.radius * 0.55, p.radius * 0.14); ctx.closePath(); ctx.fill();
      ctx.globalAlpha = 1;
    }
    // Wings
    ctx.globalAlpha = 0.65; ctx.fillStyle = s.overdriveActive ? '#ff9900' : '#0088ff';
    ctx.beginPath(); ctx.moveTo(0, -p.radius * 0.22); ctx.lineTo(-p.radius * 0.62, -p.radius * 1.1); ctx.lineTo(-p.radius * 0.88, -p.radius * 0.65); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(0, p.radius * 0.22); ctx.lineTo(-p.radius * 0.62, p.radius * 1.1); ctx.lineTo(-p.radius * 0.88, p.radius * 0.65); ctx.closePath(); ctx.fill();
    ctx.globalAlpha = 1;
    // Body
    ctx.fillStyle = pc;
    ctx.beginPath(); ctx.moveTo(p.radius * 1.15, 0); ctx.lineTo(-p.radius * 0.72, -p.radius * 0.72); ctx.lineTo(-p.radius * 0.38, 0); ctx.lineTo(-p.radius * 0.72, p.radius * 0.72); ctx.closePath(); ctx.fill();
    // Cockpit
    ctx.globalAlpha = 0.85; ctx.fillStyle = '#ffffff'; ctx.shadowBlur = 0;
    ctx.beginPath(); ctx.arc(p.radius * 0.28, 0, p.radius * 0.22, 0, Math.PI * 2); ctx.fill();
    ctx.globalAlpha = 1;
    ctx.restore(); ctx.shadowBlur = 0;
  }

  // Glow overlay from off-screen canvas
  ctx.save();
  ctx.globalAlpha = 0.52;
  ctx.filter = 'blur(11px)';
  ctx.drawImage(glowCtx.canvas, 0, 0);
  ctx.filter = 'none';
  ctx.restore();

  // ─── HUD ──────────────────────────────────────────────
  ctx.restore(); // end shake transform

  // Score
  ctx.font = `bold 28px 'Courier New',monospace`; ctx.textAlign = 'center';
  ctx.fillStyle = '#ffffff';
  ctx.shadowColor = s.overdriveActive ? '#ffdd00' : '#00f5ff'; ctx.shadowBlur = 12;
  ctx.fillText(Math.floor(s.score).toLocaleString(), W / 2, 54);
  ctx.shadowBlur = 0;
  ctx.font = `11px 'Courier New',monospace`; ctx.fillStyle = '#44556688';
  ctx.fillText(`WAVE ${s.wave}  ·  ${Math.floor(s.time)}s  ·  ${s.totalKills} KILLS`, W / 2, 70);
  ctx.textAlign = 'left';

  // HP bar
  const hbX = 20, hbY = H - 112, hbW = 148, hbH = 10;
  ctx.fillStyle = 'rgba(0,0,0,0.62)'; ctx.fillRect(hbX, hbY, hbW, hbH);
  const hpFr = p.hp / p.maxHp;
  const hpCol = hpFr > 0.5 ? '#00ff88' : hpFr > 0.25 ? '#ffaa00' : '#ff2244';
  ctx.fillStyle = hpCol; ctx.shadowColor = hpCol; ctx.shadowBlur = 7;
  ctx.fillRect(hbX, hbY, hbW * hpFr, hbH); ctx.shadowBlur = 0;
  ctx.strokeStyle = hpCol; ctx.lineWidth = 1; ctx.strokeRect(hbX, hbY, hbW, hbH);
  ctx.font = `bold 8.5px 'Courier New',monospace`; ctx.fillStyle = '#8899aa';
  ctx.fillText(`HP  ${Math.ceil(p.hp)} / ${p.maxHp}`, hbX, hbY - 5);

  // Triple Shot indicator
  if (p.tripleShot > 0) {
    const tsY = hbY + 16;
    const tsFr = clamp(p.tripleShot / TRIPLE_SHOT_DURATION, 0, 1);
    const blinking = p.tripleShot < 2.5 && Math.floor(t * 5) % 2 === 0;
    if (!blinking) {
      ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(hbX, tsY, hbW, 7);
      ctx.fillStyle = '#a259ff'; ctx.shadowColor = '#a259ff'; ctx.shadowBlur = 8;
      ctx.fillRect(hbX, tsY, hbW * tsFr, 7);
      ctx.shadowBlur = 0;
      ctx.strokeStyle = '#a259ff'; ctx.lineWidth = 1; ctx.strokeRect(hbX, tsY, hbW, 7);
      ctx.font = `bold 8px 'Courier New',monospace`;
      ctx.fillStyle = '#cc99ff'; ctx.shadowColor = '#a259ff'; ctx.shadowBlur = 6;
      ctx.fillText(`✦ TRIPLE SHOT  ${Math.ceil(p.tripleShot)}s`, hbX, tsY - 4);
      ctx.shadowBlur = 0;
    }
  }

  // Overdrive bar
  const odX = W - 168, odY = H - 112, odW = 148, odH = 10;
  const odReady = s.overdriveEnergy >= 1 && !s.overdriveActive;
  ctx.fillStyle = 'rgba(0,0,0,0.62)'; ctx.fillRect(odX, odY, odW, odH);
  const odCol = s.overdriveActive ? '#ffdd00' : odReady ? `hsl(${30 + Math.sin(t * 6) * 20},100%,55%)` : '#cc5500';
  ctx.globalAlpha = (s.overdriveActive || odReady) ? 0.6 + Math.sin(t * 8) * 0.4 : 1;
  ctx.fillStyle = odCol; ctx.shadowColor = odCol; ctx.shadowBlur = odReady ? 16 : 5;
  ctx.fillRect(odX, odY, odW * clamp(s.overdriveActive ? s.overdriveDur / 6 : s.overdriveEnergy, 0, 1), odH);
  ctx.globalAlpha = 1; ctx.shadowBlur = 0;
  ctx.strokeStyle = odCol; ctx.lineWidth = 1; ctx.strokeRect(odX, odY, odW, odH);
  ctx.font = `bold 8.5px 'Courier New',monospace`; ctx.fillStyle = odReady ? '#ffdd00' : '#7788aa';
  ctx.textAlign = 'right';
  if (odReady) { ctx.shadowColor = '#ffdd00'; ctx.shadowBlur = 10; }
  ctx.fillText(s.overdriveActive ? '⚡ ACTIVE' : odReady ? '⚡ OVERDRIVE READY' : 'OVERDRIVE', odX + odW, odY - 5);
  ctx.shadowBlur = 0; ctx.textAlign = 'left';

  // Combo display
  if (s.combo > 0) {
    const ca = clamp(s.comboTimer / 0.7, 0.25, 1);
    ctx.globalAlpha = ca;
    const cs = s.combo >= 60 ? '#ff00ff' : s.combo >= 30 ? '#ffdd00' : '#00ff88';
    ctx.font = `bold ${clamp(16 + s.combo * 0.38, 16, 34)}px 'Courier New',monospace`;
    ctx.fillStyle = cs; ctx.textAlign = 'center';
    ctx.shadowColor = cs; ctx.shadowBlur = 22;
    ctx.fillText(`×${s.combo} COMBO`, W / 2, H / 2 - 102);
    const cf = 1 + clamp(s.combo * 0.05, 0, 4);
    ctx.font = `bold 11px 'Courier New',monospace`;
    ctx.fillStyle = '#aabbcc'; ctx.shadowBlur = 0;
    ctx.fillText(`×${cf.toFixed(2)} SCORE MULTIPLIER`, W / 2, H / 2 - 83);
    const ctW = 130;
    ctx.fillStyle = 'rgba(255,255,255,0.1)'; ctx.fillRect(W / 2 - ctW / 2, H / 2 - 74, ctW, 3);
    ctx.fillStyle = cs; ctx.fillRect(W / 2 - ctW / 2, H / 2 - 74, ctW * (s.comboTimer / s.comboWindow), 3);
    ctx.shadowBlur = 0; ctx.textAlign = 'left'; ctx.globalAlpha = 1;
  }

  // Low HP vignette
  if (p.hp < 30) {
    const pulse = 0.28 + Math.sin(t * 4.5) * 0.22;
    ctx.globalAlpha = pulse;
    const vig = ctx.createRadialGradient(W / 2, H / 2, H * 0.15, W / 2, H / 2, W * 0.85);
    vig.addColorStop(0, 'transparent'); vig.addColorStop(1, 'rgba(255,20,50,0.55)');
    ctx.fillStyle = vig; ctx.fillRect(0, 0, W, H);
    ctx.globalAlpha = 1;
  }
}

/* ═══════════════════════════════════════════════════════════
   COMPONENT
═══════════════════════════════════════════════════════════ */
export default function OverdriveArena() {
  const canvasRef = useRef(null);
  const glowRef = useRef(null);
  const stateRef = useRef(null);
  const animRef = useRef(null);
  const [phase, setPhase] = useState('menu');
  const [finalScore, setFinalScore] = useState(0);
  const [highScore, setHighScore] = useState(() => loadLocalHighscore().score);
  const [highCombo, setHighCombo] = useState(() => loadLocalHighscore().combo);
  const [isNewRecord, setIsNewRecord] = useState(false);
  // ── Revive / AdMob state ──────────────────────────────────────────────────
  const [canRevive, setCanRevive] = useState(true);       // false after one revive per run
  const [adPhase, setAdPhase] = useState('idle');         // 'idle' | 'showing' | 'done'
  const reviveSnapshotRef = useRef(null);                 // { score, wave } saved at death
  const joyActiveRef = useRef(false);
  const joyIdRef = useRef(null);
  const joyBaseRef = useRef({ x: 0, y: 0 });
  const [joyDisplay, setJoyDisplay] = useState(null);

  const startGame = useCallback(() => {
    audioEngine.stop();
    audioEngine.init();
    stateRef.current = initState();
    joyActiveRef.current = false; joyIdRef.current = null;
    setIsNewRecord(false);
    setCanRevive(true);
    setAdPhase('idle');
    reviveSnapshotRef.current = null;
    setPhase('playing');
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const glowCanvas = glowRef.current;
    const glowCtx = glowCanvas.getContext('2d');
    let last = performance.now();
    const loop = (now) => {
      const dt = Math.min((now - last) / 1000, 0.05); last = now;
      const s = stateRef.current; if (!s) return;
      update(s, dt, audioEngine);
      render(ctx, glowCtx, s, s.time);
      if (s.player.hp <= 0) {
        audioEngine.stop();
        const finalSc = Math.floor(s.score);
        const finalCombo = s.combo;
        const finalWave = s.wave;
        setFinalScore(finalSc);
        // Save snapshot so revive can restore exact state
        reviveSnapshotRef.current = { score: finalSc, wave: finalWave };
        const isRecord = saveLocalHighscore(finalSc, finalCombo, finalWave);
        setIsNewRecord(isRecord);
        if (isRecord) {
          setHighScore(finalSc);
          setHighCombo(finalCombo);
        }
        submitScoreToServer(finalSc, finalCombo, finalWave); // server stub
        setPhase('dead'); return;
      }
      animRef.current = requestAnimationFrame(loop);
    };
    animRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(animRef.current);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const iv = setInterval(() => {
      setJoyDisplay(joyActiveRef.current ? { base: { ...joyBaseRef.current } } : null);
    }, 33);
    return () => clearInterval(iv);
  }, [phase]);

  const activateOverdrive = useCallback(() => {
    const s = stateRef.current; if (!s) return;
    if (s.overdriveEnergy >= 1 && !s.overdriveActive) {
      s.overdriveActive = true; s.overdriveEnergy = 0; s.overdriveDur = 6;
      s.glitch = 0.95; s.shake = { x: 0, y: 0, power: 22, dur: 0.5 };
      audioEngine.activateOverdrive();
      s.particles.push(
        ...makeParticles(s.player.x, s.player.y, '#ffdd00', 55, 2, 6.5, 0.9),
        ...makeParticles(s.player.x, s.player.y, '#ffffff', 22, 1.2, 3, 0.5),
        makeRing(s.player.x, s.player.y, '#ffdd00', 130),
        makeRing(s.player.x, s.player.y, '#ff8800', 90),
        { x: W / 2, y: H / 2 - 90, vx: 0, vy: -0.3, life: 1.7, maxLife: 1.7, color: '#ffdd00', size: 0, isText: true, text: '⚡ OVERDRIVE! ⚡' },
      );
    }
  }, []);

  const doDash = useCallback(() => {
    const s = stateRef.current; if (!s) return;
    const p = s.player; if (p.dashCd > 0) return;
    let dx = s.input.dx, dy = s.input.dy;
    if (!dx && !dy) { dx = Math.cos(p.angle); dy = Math.sin(p.angle); }
    const dir = norm({ x: dx, y: dy });
    p.dashVx = dir.x; p.dashVy = dir.y; p.dashDur = 0.12; p.dashCd = 2.8; p.invincible = 0.14;
    audioEngine.dashSound();
    s.particles.push(...makeParticles(p.x, p.y, '#00f5ff', 8, 0.8, 3, 0.3), makeRing(p.x, p.y, '#00f5ff', 28));
  }, []);

  // ── Revive logic ─────────────────────────────────────────────────────────
  // onAdRewarded: called by showRewardedAd() when the player earns the reward.
  // This is the function your app-developer should NOT need to touch.
  const onAdRewarded = useCallback(() => {
    setAdPhase('done');
    const snapshot = reviveSnapshotRef.current;
    if (!snapshot) return;

    // Re-init game state but restore score + wave from the death snapshot
    const fresh = initState();
    fresh.score = snapshot.score;
    fresh.wave  = snapshot.wave;
    fresh.waveTimer = 0;           // reset wave timer so next wave isn't instant
    fresh.player.hp = fresh.player.maxHp; // full HP
    stateRef.current = fresh;

    setCanRevive(false);           // one revive per run
    setIsNewRecord(false);
    setAdPhase('idle');
    audioEngine.stop();
    audioEngine.init();
    setPhase('playing');
  }, []);

  // handleRevive: called when the player taps "Watch Ad to Revive".
  // Shows the simulated (or real) ad then triggers onAdRewarded on completion.
  const handleRevive = useCallback(() => {
    if (!canRevive || adPhase !== 'idle') return;
    setAdPhase('showing');
    showRewardedAd(onAdRewarded);
  }, [canRevive, adPhase, onAdRewarded]);

  const handleTouchStart = useCallback((e) => {
    audioEngine.init();
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = W / rect.width, scaleY = H / rect.height;
    for (const touch of e.changedTouches) {
      const tx = (touch.clientX - rect.left) * scaleX;
      const ty = (touch.clientY - rect.top) * scaleY;
      if (tx < W / 2) {
        if (!joyActiveRef.current) {
          joyActiveRef.current = true; joyIdRef.current = touch.identifier;
          joyBaseRef.current = { x: tx, y: ty };
        }
      } else {
        const s = stateRef.current;
        if (s && s.overdriveEnergy >= 1 && !s.overdriveActive) activateOverdrive(); else doDash();
      }
    }
    e.preventDefault();
  }, [activateOverdrive, doDash]);

  const handleTouchMove = useCallback((e) => {
    const canvas = canvasRef.current; if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scaleX = W / rect.width, scaleY = H / rect.height;
    const joyRadius = 60;
    for (const touch of e.changedTouches) {
      if (touch.identifier === joyIdRef.current) {
        const tx = (touch.clientX - rect.left) * scaleX;
        const ty = (touch.clientY - rect.top) * scaleY;
        let dx = tx - joyBaseRef.current.x, dy = ty - joyBaseRef.current.y;
        const d = Math.sqrt(dx * dx + dy * dy);
        if (d > joyRadius) { dx = dx / d * joyRadius; dy = dy / d * joyRadius; }
        const s = stateRef.current;
        if (s) { s.input.dx = dx / joyRadius; s.input.dy = dy / joyRadius; }
      }
    }
    e.preventDefault();
  }, []);

  const handleTouchEnd = useCallback((e) => {
    for (const touch of e.changedTouches) {
      if (touch.identifier === joyIdRef.current) {
        joyActiveRef.current = false; joyIdRef.current = null;
        const s = stateRef.current; if (s) { s.input.dx = 0; s.input.dy = 0; }
      }
    }
    e.preventDefault();
  }, []);

  useEffect(() => {
    if (phase !== 'playing') return;
    const keys = {};
    const onDown = (e) => {
      keys[e.key] = true;
      if (e.key === ' ' || e.key === 'f') { e.preventDefault(); activateOverdrive(); }
      if (e.key === 'Shift' || e.key === 'x') doDash();
    };
    const onUp = (e) => { delete keys[e.key]; };
    window.addEventListener('keydown', onDown); window.addEventListener('keyup', onUp);
    const iv = setInterval(() => {
      if (joyActiveRef.current) return;
      const s = stateRef.current; if (!s) return;
      let dx = 0, dy = 0;
      if (keys['ArrowLeft'] || keys['a'] || keys['A']) dx -= 1;
      if (keys['ArrowRight'] || keys['d'] || keys['D']) dx += 1;
      if (keys['ArrowUp'] || keys['w'] || keys['W']) dy -= 1;
      if (keys['ArrowDown'] || keys['s'] || keys['S']) dy += 1;
      if (dx || dy) { const dir = norm({ x: dx, y: dy }); s.input.dx = dir.x; s.input.dy = dir.y; }
      else { s.input.dx = 0; s.input.dy = 0; }
    }, 16);
    return () => { window.removeEventListener('keydown', onDown); window.removeEventListener('keyup', onUp); clearInterval(iv); };
  }, [phase, activateOverdrive, doDash]);

  const scale = typeof window !== 'undefined' ? Math.min(window.innerWidth / W, window.innerHeight / H, 1) : 1;
  const joyR = 60;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#02020a', fontFamily: "'Courier New',monospace", userSelect: 'none', WebkitUserSelect: 'none', overflow: 'hidden' }}>

      {/* MENU */}
      {phase === 'menu' && (
        <div style={{ textAlign: 'center', color: '#fff', padding: '24px 20px', maxWidth: 390 }}>
          <div style={{ fontSize: 42, fontWeight: 'bold', letterSpacing: 4, color: '#00f5ff', textShadow: '0 0 30px #00f5ff,0 0 70px rgba(0,245,255,0.35)', marginBottom: 6, lineHeight: 1.15 }}>
            OVERDRIVE<br />ARENA
          </div>
          <div style={{ fontSize: 11, color: '#334466', letterSpacing: 5, marginBottom: 8 }}>v2.0 — FULL AUDIO EDITION</div>
          <div style={{ width: 200, height: 1, background: 'linear-gradient(to right,transparent,#00f5ff,transparent)', margin: '0 auto 28px' }} />
          <div style={{ background: 'rgba(0,245,255,0.04)', border: '1px solid rgba(0,245,255,0.12)', borderRadius: 4, padding: '14px 18px', marginBottom: 28, textAlign: 'left' }}>
            <div style={{ fontSize: 11, color: '#6688aa', lineHeight: 2.1 }}>
              <span style={{ color: '#00f5ff' }}>📱 LEFT</span> Joystick · <span style={{ color: '#ffdd00' }}>RIGHT</span> Dash / Overdrive<br />
              <span style={{ color: '#00f5ff' }}>⌨️ WASD</span> Move · <span style={{ color: '#ffdd00' }}>SPACE</span> Overdrive · <span style={{ color: '#00f5ff' }}>SHIFT</span> Dash<br />
              <span style={{ color: '#ff00ff' }}>★</span> Auto-aim · Build combos · Chain Overdrive!<br />
              <span style={{ color: '#ffdd00' }}>🔊</span> Procedural audio synth — zero file dependencies
            </div>
          </div>
          <button onClick={startGame} style={{ display: 'block', width: '100%', padding: '17px', fontSize: 18, fontWeight: 'bold', fontFamily: "'Courier New',monospace", letterSpacing: 4, background: 'transparent', color: '#00f5ff', border: '2px solid #00f5ff', cursor: 'pointer', textShadow: '0 0 15px #00f5ff', boxShadow: '0 0 30px rgba(0,245,255,0.18),inset 0 0 30px rgba(0,245,255,0.05)', marginBottom: 12 }}>
            ▶ START RUN
          </button>
          {highScore > 0 && (
            <div style={{ fontSize: 13, color: '#ffdd00', textShadow: '0 0 8px #ffdd00', marginTop: 6 }}>
              ★ BEST: {highScore.toLocaleString()}  ·  MAX COMBO: ×{highCombo}
            </div>
          )}
        </div>
      )}

      {/* GAME CANVAS */}
      {(phase === 'playing' || phase === 'dead') && (
        <div style={{ position: 'relative', width: W * scale, height: H * scale, flexShrink: 0 }}>
          <canvas ref={glowRef} width={W} height={H} style={{ position: 'absolute', top: 0, left: 0, display: 'none' }} />
          <canvas ref={canvasRef} width={W} height={H}
            style={{ display: 'block', width: W * scale, height: H * scale, touchAction: 'none' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          />
          {joyDisplay && (
            <svg style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }} viewBox={`0 0 ${W} ${H}`}>
              <circle cx={joyDisplay.base.x} cy={joyDisplay.base.y} r={joyR} fill="rgba(0,245,255,0.06)" stroke="rgba(0,245,255,0.22)" strokeWidth="1.5" />
              <circle cx={joyDisplay.base.x} cy={joyDisplay.base.y} r={13} fill="rgba(0,245,255,0.15)" stroke="rgba(0,245,255,0.5)" strokeWidth="1.5" />
            </svg>
          )}
          {phase === 'playing' && (
            <>
              <div style={{ position: 'absolute', bottom: 122 * scale, left: 8 * scale, fontSize: 9 * scale, color: 'rgba(0,245,255,0.2)', pointerEvents: 'none' }}>◉ MOVE</div>
              <div style={{ position: 'absolute', bottom: 122 * scale, right: 8 * scale, fontSize: 9 * scale, color: 'rgba(255,200,0,0.2)', pointerEvents: 'none', textAlign: 'right' }}>TAP ►<br />DASH/OD</div>
            </>
          )}
        </div>
      )}

      {/* DEATH SCREEN */}
      {phase === 'dead' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'rgba(2,2,14,0.95)', fontFamily: "'Courier New',monospace" }}>

          {/* ── Simulated Ad Overlay ─────────────────────────────────────── */}
          {adPhase === 'showing' && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 300, background: '#000', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: 13, color: '#555', letterSpacing: 3, marginBottom: 18 }}>YOUR AD HERE</div>
              <div style={{ fontSize: 22, color: '#ffffff', fontWeight: 'bold', letterSpacing: 2, textShadow: '0 0 14px #fff', marginBottom: 8 }}>Simulating Ad...</div>
              <div style={{ fontSize: 11, color: '#444', letterSpacing: 2 }}>Please wait 3 seconds</div>
              {/* Fake progress bar */}
              <div style={{ marginTop: 28, width: 200, height: 3, background: '#222', borderRadius: 2, overflow: 'hidden' }}>
                <div style={{ height: '100%', background: '#ffdd00', borderRadius: 2, animation: 'adProgress 3s linear forwards' }} />
              </div>
              <style>{`@keyframes adProgress { from { width: 0% } to { width: 100% } }`}</style>
            </div>
          )}

          {/* ── Game Over content ─────────────────────────────────────────── */}
          <div style={{ fontSize: 30, color: '#ff2244', fontWeight: 'bold', letterSpacing: 4, textShadow: '0 0 26px #ff2244', marginBottom: 6 }}>RUN OVER</div>
          <div style={{ width: 220, height: 1, background: 'linear-gradient(to right,transparent,#ff2244,transparent)', marginBottom: 26 }} />
          <div style={{ fontSize: 50, fontWeight: 'bold', color: '#00f5ff', textShadow: '0 0 28px #00f5ff', marginBottom: 4 }}>{finalScore.toLocaleString()}</div>
          <div style={{ fontSize: 11, color: '#334466', letterSpacing: 3, marginBottom: 26 }}>FINAL SCORE</div>
          {isNewRecord && (
            <div style={{ fontSize: 13, color: '#ffdd00', textShadow: '0 0 14px #ffdd00', marginBottom: 22, letterSpacing: 2 }}>★ NEW PERSONAL BEST ★</div>
          )}

          {/* ── Revive button (shown only if canRevive and ad isn't running) ── */}
          {canRevive && adPhase === 'idle' && (
            <button
              onClick={handleRevive}
              style={{ width: 260, padding: '15px', fontSize: 15, fontWeight: 'bold', fontFamily: "'Courier New',monospace", letterSpacing: 2, background: 'rgba(255,215,0,0.08)', color: '#ffdd00', border: '2px solid #ffdd00', cursor: 'pointer', textShadow: '0 0 10px #ffdd00', boxShadow: '0 0 22px rgba(255,221,0,0.2),inset 0 0 22px rgba(255,221,0,0.05)', marginBottom: 12 }}>
              📺 Watch Ad to Revive
            </button>
          )}

          <button onClick={startGame} style={{ width: 260, padding: '15px', fontSize: 17, fontWeight: 'bold', fontFamily: "'Courier New',monospace", letterSpacing: 4, background: 'transparent', color: '#00f5ff', border: '2px solid #00f5ff', cursor: 'pointer', textShadow: '0 0 12px #00f5ff', boxShadow: '0 0 22px rgba(0,245,255,0.14)', marginBottom: 12 }}>▶ TRY AGAIN</button>
          <button onClick={() => setPhase('menu')} style={{ width: 260, padding: '10px', fontSize: 12, fontFamily: "'Courier New',monospace", letterSpacing: 2, background: 'transparent', color: '#334455', border: '1px solid #223344', cursor: 'pointer' }}>MAIN MENU</button>
          <div style={{ marginTop: 22, fontSize: 11, color: '#334466', letterSpacing: 1 }}>BEST: {highScore.toLocaleString()} · MAX COMBO: ×{highCombo}</div>
        </div>
      )}
    </div>
  );
}
